/**
 * 视频校验工具类
 */

/**
 * 校验失败类型枚举
 */
export enum VideoInvalid {
  /** 文件类型错误 */
  FILE_TYPE = 'FILE_TYPE',
  /** 文件大小错误 */
  FILE_SIZE = 'FILE_SIZE',
  /** 视频时长错误 */
  DURATION = 'DURATION',
  /** 视频分辨率错误 */
  RESOLUTION = 'RESOLUTION',
  /** 未知错误 */
  UNKNOWN = 'UNKNOWN'
}

/**
 * 校验结果接口
 */
export interface ValidationResult {
  isValid: boolean;
  errorType?: VideoInvalid;
  errorMessage?: string;
}

export interface VideoValidationOptions {
  minDuration?: number; // 最小时长（秒）
  maxDuration?: number; // 最大时长（秒）
  minWidth?: number;    // 最小宽度（像素）
  minHeight?: number;   // 最小高度（像素）
  maxWidth?: number;    // 最大宽度（像素）
  maxHeight?: number;   // 最大高度（像素）
  maxFileSize?: number; // 最大文件大小（字节）
  minFileSize?: number; // 最小文件大小（字节）
  allowedTypes?: string[]; // 允许的文件类型（MIME类型）
}

export interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
  fileSize: number;
  fileType: string;
}

/**
 * 获取视频元数据
 * @param file 视频文件
 * @returns Promise<VideoMetadata>
 */
export function getVideoMetadata(file: File): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    
    video.onloadedmetadata = () => {
      const metadata: VideoMetadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        fileSize: file.size,
        fileType: file.type,
      };
      
      // 清理资源
      URL.revokeObjectURL(url);
      resolve(metadata);
    };
    
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = url;
  });
}

/**
 * 校验视频时长
 * @param duration 视频时长（秒）
 * @param minDuration 最小时长（秒）
 * @param maxDuration 最大时长（秒）
 * @returns 校验结果
 */
export function validateVideoDuration(
  duration: number,
  minDuration?: number,
  maxDuration?: number
): boolean {
  if (minDuration !== undefined && duration < minDuration) {
    return false;
  }
  
  if (maxDuration !== undefined && duration > maxDuration) {
    return false;
  }
  
  return true;
}

/**
 * 校验视频分辨率
 * @param width 视频宽度
 * @param height 视频高度
 * @param options 分辨率限制选项
 * @returns 校验结果
 */
export function validateVideoResolution(
  width: number,
  height: number,
  options: {
    minWidth?: number;
    minHeight?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): boolean {
  const { minWidth, minHeight, maxWidth, maxHeight } = options;
  
  if (minWidth !== undefined && width < minWidth) {
    return false;
  }
  
  if (minHeight !== undefined && height < minHeight) {
    return false;
  }
  
  if (maxWidth !== undefined && width > maxWidth) {
    return false;
  }
  
  if (maxHeight !== undefined && height > maxHeight) {
    return false;
  }
  
  return true;
}

/**
 * 校验文件大小
 * @param fileSize 文件大小（字节）
 * @param minSize 最小文件大小（字节）
 * @param maxSize 最大文件大小（字节）
 * @returns 校验结果
 */
export function validateFileSize(
  fileSize: number,
  minSize?: number,
  maxSize?: number
): boolean {
  if (minSize !== undefined && fileSize < minSize) {
    return false;
  }
  
  if (maxSize !== undefined && fileSize > maxSize) {
    return false;
  }
  
  return true;
}

/**
 * 校验文件类型
 * @param fileType 文件MIME类型
 * @param allowedTypes 允许的文件类型数组 为空则都允许
 * @returns 校验结果
 */
export function validateFileType(
  fileType: string,
  allowedTypes: string[] = []
): boolean {
  return allowedTypes.length === 0 || allowedTypes.includes(fileType);
}

/**
 * 综合校验视频文件
 * @param file 视频文件
 * @param options 校验选项
 * @returns Promise<ValidationResult>
 */
export async function validateVideoReason(
  file: File,
  options: VideoValidationOptions = {}
): Promise<ValidationResult> {  
  try {
    // 首先进行文件类型校验（不需要加载视频元数据）
    if (options.allowedTypes) {
      const typeResult = validateFileType(file.type, options.allowedTypes);
      if (!typeResult) {
        return {
          isValid: false,
          errorType: VideoInvalid.FILE_TYPE,
          errorMessage: `File type ${file.type} is not allowed`
        };
      }
    }
    
    // 文件大小校验（不需要加载视频元数据）
    if (options.minFileSize !== undefined || options.maxFileSize !== undefined) {
      const sizeResult = validateFileSize(
        file.size,
        options.minFileSize,
        options.maxFileSize
      );
      
      if (!sizeResult) {
        console.log('File size out of range', { fileSize: file.size, minSize: options.minFileSize, maxSize: options.maxFileSize });
        return {
          isValid: false,
          errorType: VideoInvalid.FILE_SIZE,
          errorMessage: `File size ${file.size} bytes is out of range`
        };
      }
    }
    
    // 获取视频元数据进行进一步校验
    const metadata = await getVideoMetadata(file);
    
    // 校验时长
    if (options.minDuration !== undefined || options.maxDuration !== undefined) {
      const durationResult = validateVideoDuration(
        metadata.duration,
        options.minDuration,
        options.maxDuration
      );
      
      if (!durationResult) {
        console.log('Video duration out of range', { duration: metadata.duration, minDuration: options.minDuration, maxDuration: options.maxDuration });
        return {
          isValid: false,
          errorType: VideoInvalid.DURATION,
          errorMessage: `Video duration ${metadata.duration}s is out of range`
        };
      }
    }
    
    // 校验分辨率
    const resolutionOptions = {
      minWidth: options.minWidth,
      minHeight: options.minHeight,
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
    };
    
    if (Object.values(resolutionOptions).some(val => val !== undefined)) {
      const resolutionResult = validateVideoResolution(
        metadata.width,
        metadata.height,
        resolutionOptions
      );
      
      if (!resolutionResult) {
        console.log('Video resolution out of range', { width: metadata.width, height: metadata.height, minWidth: options.minWidth, minHeight: options.minHeight, maxWidth: options.maxWidth, maxHeight: options.maxHeight });
        return {
          isValid: false,
          errorType: VideoInvalid.RESOLUTION,
          errorMessage: `Video resolution ${metadata.width}x${metadata.height} is out of range`
        };
      }
    }
    
    return {
      isValid: true
    };
  } catch (error) {
    return {
      isValid: false,
      errorType: VideoInvalid.UNKNOWN,
      errorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}
